const AVATAR_FALLBACK_STORAGE_KEY = 'avatar_fallback_colors';
const AVATAR_FALLBACK_START_COLORS: readonly string[] = [
	'#5BC78A',
	'#36E278',
	'#FF8A3D',
	'#3FB7E8',
	'#E0533D',
	'#9BE36B',
	'#5583F6',
];
const AVATAR_FALLBACK_END_COLOR = '#B87DDB';
const AVATAR_FALLBACK_MAX_STORED_ENTRIES = 1000;

let startColorByUserId: Map<string, string> | null = null;
const usageByStartColor = new Map<string, number>(
	AVATAR_FALLBACK_START_COLORS.map((color) => [color, 0])
);

function loadStoredStartColors(): Map<string, string> {
	const loaded = new Map<string, string>();
	if (typeof window === 'undefined') {
		return loaded;
	}

	try {
		const parsed: unknown = JSON.parse(localStorage.getItem(AVATAR_FALLBACK_STORAGE_KEY) || 'null');
		if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
			for (const [userId, color] of Object.entries(parsed)) {
				if (typeof color === 'string' && AVATAR_FALLBACK_START_COLORS.includes(color)) {
					loaded.set(userId, color);
				}
			}
		}
	} catch {}

	return loaded;
}

function getStartColorCache(): Map<string, string> {
	if (!startColorByUserId) {
		startColorByUserId = loadStoredStartColors();
		for (const color of startColorByUserId.values()) {
			usageByStartColor.set(color, (usageByStartColor.get(color) ?? 0) + 1);
		}
	}
	return startColorByUserId;
}

function persistStartColors(cache: Map<string, string>): void {
	if (typeof window === 'undefined') {
		return;
	}

	try {
		localStorage.setItem(AVATAR_FALLBACK_STORAGE_KEY, JSON.stringify(Object.fromEntries(cache)));
	} catch {}
}

function pickRandom<T>(items: readonly T[]): T {
	return items[Math.floor(Math.random() * items.length)];
}

function pickLeastUsedStartColor(): string {
	const minUsage = Math.min(...usageByStartColor.values());
	const leastUsed = AVATAR_FALLBACK_START_COLORS.filter(
		(color) => usageByStartColor.get(color) === minUsage
	);
	return pickRandom(leastUsed);
}

function buildBackground(start: string): string {
	return `linear-gradient(135deg, ${start} 0%, ${AVATAR_FALLBACK_END_COLOR} 100%)`;
}

export function getAvatarFallbackBackground(userId: string): string {
	const cache = getStartColorCache();
	const cached = cache.get(userId);
	if (cached) {
		return buildBackground(cached);
	}

	const start = pickLeastUsedStartColor();
	usageByStartColor.set(start, (usageByStartColor.get(start) ?? 0) + 1);
	cache.set(userId, start);

	if (cache.size > AVATAR_FALLBACK_MAX_STORED_ENTRIES) {
		const oldestUserId = cache.keys().next().value;
		if (oldestUserId !== undefined) {
			const oldestColor = cache.get(oldestUserId);
			cache.delete(oldestUserId);
			if (oldestColor) {
				usageByStartColor.set(
					oldestColor,
					Math.max((usageByStartColor.get(oldestColor) ?? 1) - 1, 0)
				);
			}
		}
	}

	persistStartColors(cache);
	return buildBackground(start);
}
